/**
 * Ders değerlendirme raporu — manevra sayaçları ve yıldızlı değerlendirme maddeleri.
 * Ürün spec v0.2; metinler Türkçe ve kodlar API ile uyumludur.
 */

export type ManeuverCode =
  | "arac_tanitim_ic_dis"
  | "arac_calistirma_surus_ayari"
  | "sinyal_kullanim"
  | "vites_gecis_debriyaj"
  | "dur_kalk_rampa_kalkis"
  | "l_park"
  | "paralel_park"
  | "ani_fren"
  | "geri_surus"
  | "u_donus_ada_donus";

export type RatingCriterionCode =
  | "direksiyon_hakimiyeti"
  | "vites_kullanimi"
  | "l_park"
  | "paralel_park"
  | "geri_surus"
  | "serit_degistirme"
  | "viraj_hakimiyeti"
  | "hiz_kontrolu"
  | "sinyal_kullanimi"
  | "ayna_kullanimi"
  | "trafik_uyumu"
  | "rampa_kalkis"
  | "serit_takip_degistirme"
  | "arac_takip_gecme"
  | "sehir_ici_kullanim"
  | "sehir_disi_kullanim"
  | "gece_surusu";

export const MANEUVER_ITEMS: ReadonlyArray<{ code: ManeuverCode; label: string }> = [
  { code: "arac_tanitim_ic_dis", label: "Araç Tanıtım (İç-Dış)" },
  { code: "arac_calistirma_surus_ayari", label: "Araç Çalıştırma (Sürüş Ayarı)" },
  { code: "sinyal_kullanim", label: "Sinyal Kullanımı" },
  { code: "vites_gecis_debriyaj", label: "Vites Geçişi – Debriyaj" },
  { code: "dur_kalk_rampa_kalkis", label: "Dur/Kalk – Rampa Kalkışı" },
  { code: "l_park", label: "L Park" },
  { code: "paralel_park", label: "Paralel Park" },
  { code: "ani_fren", label: "Ani Fren" },
  { code: "geri_surus", label: "Geri Sürüş" },
  { code: "u_donus_ada_donus", label: "U Dönüşü – Ada Dönüşü" },
] as const;

/** Matris tablo başlıkları — iki satır */
export const MANEUVER_TABLE_HEADERS: Record<ManeuverCode, { line1: string; line2: string }> = {
  arac_tanitim_ic_dis: { line1: "Araç", line2: "Tanıtım" },
  arac_calistirma_surus_ayari: { line1: "Araç", line2: "Çalıştırma" },
  sinyal_kullanim: { line1: "Sinyal", line2: "Kullanımı" },
  vites_gecis_debriyaj: { line1: "Vites", line2: "Geçişi" },
  dur_kalk_rampa_kalkis: { line1: "Dur/Kalk", line2: "Rampa Kalkışı" },
  l_park: { line1: "L", line2: "Park" },
  paralel_park: { line1: "Paralel", line2: "Park" },
  ani_fren: { line1: "Ani", line2: "Fren" },
  geri_surus: { line1: "Geri", line2: "Sürüş" },
  u_donus_ada_donus: { line1: "U Dönüşü", line2: "Ada Dönüşü" },
};

/** Liste ve özet görünümlerde kompakt etiket */
export const MANEUVER_SHORT_LABELS: Record<ManeuverCode, string> = {
  arac_tanitim_ic_dis: "Tanıtım",
  arac_calistirma_surus_ayari: "Çalıştırma",
  sinyal_kullanim: "Sinyal",
  vites_gecis_debriyaj: "Vites",
  dur_kalk_rampa_kalkis: "Dur/Kalk",
  l_park: "L Park",
  paralel_park: "Paralel",
  ani_fren: "Ani Fren",
  geri_surus: "Geri",
  u_donus_ada_donus: "U Dönüşü",
};

export const RATING_CRITERIA: ReadonlyArray<{ code: RatingCriterionCode; label: string }> = [
  { code: "direksiyon_hakimiyeti", label: "Direksiyon Hakimiyeti" },
  { code: "vites_kullanimi", label: "Vites Kullanımı" },
  { code: "l_park", label: "L Park" },
  { code: "paralel_park", label: "Paralel Park" },
  { code: "geri_surus", label: "Geri Sürüş" },
  { code: "serit_degistirme", label: "Şerit Değiştirme" },
  { code: "viraj_hakimiyeti", label: "Viraj Hakimiyeti" },
  { code: "hiz_kontrolu", label: "Hız Kontrolü" },
  { code: "sinyal_kullanimi", label: "Sinyal Kullanımı" },
  { code: "ayna_kullanimi", label: "Ayna Kullanımı" },
  { code: "trafik_uyumu", label: "Trafik Uyumu" },
  { code: "rampa_kalkis", label: "Rampa Kalkışı" },
  { code: "serit_takip_degistirme", label: "Şerit Takip – Şerit Değiştirme" },
  { code: "arac_takip_gecme", label: "Araç Takip – Araç Geçme" },
  { code: "sehir_ici_kullanim", label: "Şehir İçi Kullanımı" },
  { code: "sehir_disi_kullanim", label: "Şehir Dışı Kullanımı" },
  { code: "gece_surusu", label: "Gece Sürüşü" },
] as const;

export const MANEUVER_LABELS: Record<ManeuverCode, string> = Object.fromEntries(
  MANEUVER_ITEMS.map((m) => [m.code, m.label]),
) as Record<ManeuverCode, string>;

export const RATING_CRITERION_LABELS: Record<RatingCriterionCode, string> = Object.fromEntries(
  RATING_CRITERIA.map((r) => [r.code, r.label]),
) as Record<RatingCriterionCode, string>;

/** Ders puanı: yalnızca girilen 1–5 yıldızların ortalaması. */
export function averageLessonRating(stars: Partial<Record<RatingCriterionCode, number>>): number | null {
  const values = RATING_CRITERIA.map((c) => stars[c.code]).filter(
    (v): v is number => typeof v === "number" && v >= 1 && v <= 5,
  );
  if (values.length === 0) return null;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
}
